"use client";

import { useState } from "react";
import { IconCopyOutline18, IconCircleCheckOutline18 } from "nucleo-ui-outline-18";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getExplorerAddressUrl, getExplorerTxUrl } from "@/lib/utils";
import { SOLANA_CHAIN_IDS } from "@/lib/chains";
import { isEvmAddress, isSolanaAddress, isTxHash } from "../detect";
import { PRIORITY, type ValueRenderer } from "../types";

const SOLANA_KEY_RE = /(address|wallet|account|signer|mint|owner|payer|authority|pubkey)/i;

function truncate(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

type AddrKind = "tx" | "address";

/**
 * Resolve a block-explorer link, reusing the shared chain-aware helpers in
 * `@/lib/utils` (they cover every EVM chain, Solana → solscan, and the
 * Etherscan fallback). The Etherscan fallback only applies to EVM (`0x`)
 * values — a non-EVM address never gets an Etherscan link.
 */
function explorerUrl(kind: AddrKind, value: string, chainId: number | undefined): string | null {
  // EVM addresses and tx hashes are 0x-prefixed. Default to Ethereum mainnet
  // only when no sibling chainId pins the chain.
  if (value.startsWith("0x")) {
    return kind === "tx" ? getExplorerTxUrl(chainId ?? 1, value) : getExplorerAddressUrl(chainId ?? 1, value);
  }
  // Non-EVM (Solana): only link when a Solana chainId is in context — never
  // guess an explorer for a non-0x address.
  if (chainId != null && SOLANA_CHAIN_IDS.has(chainId)) {
    return getExplorerAddressUrl(chainId, value);
  }
  return null;
}

export const AddressRenderer: ValueRenderer = {
  id: "address",
  match: (value, ctx) => {
    if (isTxHash(value) || isEvmAddress(value)) return { priority: PRIORITY.STRONG_SCALAR, confidence: "high" };
    if (isSolanaAddress(value) && ctx.key != null && SOLANA_KEY_RE.test(ctx.key)) {
      return { priority: PRIORITY.STRONG_SCALAR, confidence: "low" };
    }
    return null;
  },
  Component: ({ value, ctx }) => {
    const [copied, setCopied] = useState(false);
    const str = value as string;
    const kind: AddrKind = isTxHash(str) ? "tx" : "address";
    const chainId = typeof ctx.parent?.chainId === "number" ? (ctx.parent.chainId as number) : undefined;
    const url = explorerUrl(kind, str, chainId);

    const copy = async () => {
      // navigator.clipboard is undefined in insecure contexts / older browsers,
      // and writeText can reject on a permissions failure — guard both.
      if (!navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(str);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        // clipboard unavailable — leave the icon in its default state
      }
    };

    return (
      <span className="border-ds-border-default bg-ds-surface-card inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="mono-xs">{truncate(str)}</span>
            </TooltipTrigger>
            <TooltipContent>{str}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <button onClick={copy} className="text-ds-text-muted hover:text-ds-text-primary" aria-label="Copy">
          {copied ? <IconCircleCheckOutline18 className="h-3 w-3" /> : <IconCopyOutline18 className="h-3 w-3" />}
        </button>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ds-brand-500 text-2xs hover:underline"
          >
            ↗
          </a>
        )}
      </span>
    );
  },
};
